/**
 * CPU-side generation of the initial strand state. Pure — the only
 * randomness comes from the injected stream (prng.deriveStreams().particles),
 * so the same seed always produces the same buffer, hence the same plot.
 *
 * Layout per particle (8 floats, 32 bytes, mirrored by gl/buffers.ts):
 *   [0..3] posAgeLife: x, y (domain units), age (s, starts <= 0), lifetime (s)
 *   [4..7] rand: sizeJitter, paletteT, speedJitter, 0 (slot kept for layout compat)
 */

import type { EmissionPattern } from "../types";

export const FLOATS_PER_PARTICLE = 8;
export const BYTES_PER_PARTICLE = FLOATS_PER_PARTICLE * 4;

/**
 * The sim domain extends this far past the visible window on every side
 * (domain units). Emission coordinates are offset by BLEED so draw.vert's
 * window mapping is unchanged; strands may drift past the domain and just
 * keep going invisibly (no wrap, no respawn).
 *
 * Visible window = [BLEED, BLEED + aspect] x [BLEED, BLEED + 1].
 */
export const BLEED = 0.12;

export function lifetimeRoll(base: number, jitter: number, r: number): number {
  return base * (1 + (r - 0.5) * jitter);
}

export interface EmissionOpts {
  lifetime: number;
  lifetimeJitter: number;
  /** Strand starts spread over [0, stagger] seconds (age starts negative). */
  stagger: number;
  emission: EmissionPattern;
  plumeCount: number;
  /** Plume gaussian sigma, in units of the visible window height. */
  spread: number;
  /** Probability a plume strand uses the wide (3.5 x spread) halo gaussian. */
  halo: number;
  /** View magnification (>= 1): spawn anchors live in the ZOOMED window so
   *  compositions stay framed; pattern geometry scales down with it. */
  zoom: number;
}

const TAU = Math.PI * 2;

/** One standard gaussian pair via Box-Muller from two stream draws. */
function boxMuller(rand: () => number): [number, number] {
  // 1 - rand() lies in (0, 1]: log(0) can never happen.
  const r = Math.sqrt(-2 * Math.log(1 - rand()));
  const th = TAU * rand();
  return [r * Math.cos(th), r * Math.sin(th)];
}

interface Plume {
  x: number;
  y: number;
  cumWeight: number; // running total for the weighted pick
}

/**
 * Build the step-0 strand buffer.
 *
 * DETERMINISM CONTRACT — the stream draw order is FIXED:
 *   1. "plumes" only: per plume (in index order) cx, cy, weight.
 *   2. Per particle, in index order:
 *      a. emission position draws (per-pattern, documented at each branch)
 *      b. age stagger draw
 *      c. lifetime draw
 *      d. sizeJitter, paletteT, speedJitter draws
 * Reordering any of this silently changes every shared seed URL. (The plot
 * model rewrite intentionally reset all prior seeds — once.)
 */
export function buildInitialState(
  count: number,
  aspect: number,
  opts: EmissionOpts,
  rand: () => number,
): Float32Array {
  const out = new Float32Array(count * FLOATS_PER_PARTICLE);
  // Zoomed visible window (domain units), centered in the padded domain.
  // zoom = 1 -> origin at BLEED, size aspect x 1 (the classic window).
  const zoom = Math.max(1, opts.zoom);
  const winW = aspect / zoom;
  const winH = 1 / zoom;
  const ox = BLEED + (aspect - winW) / 2;
  const oy = BLEED + (1 - winH) / 2;
  const cx = ox + winW / 2;
  const cy = oy + winH / 2;

  // Plume definitions are drawn FIRST so per-particle draws are independent
  // of plumeCount interleaving. Centers land in the window inset 20% per side.
  let plumes: Plume[] = [];
  let plumeWeightTotal = 0;
  if (opts.emission === "plumes") {
    for (let i = 0; i < opts.plumeCount; i++) {
      const px = ox + winW * (0.2 + rand() * 0.6);
      const py = oy + winH * (0.2 + rand() * 0.6);
      plumeWeightTotal += 0.4 + rand() * 0.6;
      plumes.push({ x: px, y: py, cumWeight: plumeWeightTotal });
    }
  }

  for (let i = 0; i < count; i++) {
    const o = i * FLOATS_PER_PARTICLE;
    let px: number;
    let py: number;
    switch (opts.emission) {
      case "plumes": {
        // Draws: plume pick, halo pick, Box-Muller u1, u2.
        const pick = rand() * plumeWeightTotal;
        let plume = plumes[plumes.length - 1]!;
        for (const p of plumes) {
          if (pick < p.cumWeight) {
            plume = p;
            break;
          }
        }
        const wide = rand() < opts.halo;
        const sigma = (wide ? 3.5 * opts.spread : opts.spread) * winH;
        const [gx, gy] = boxMuller(rand);
        // May land outside the window or even the domain — fine, no clamp.
        px = plume.x + gx * sigma;
        py = plume.y + gy * sigma;
        break;
      }
      case "center": {
        // Draws: radius, theta. Disc r = 0.18*sqrt(u) about the center.
        const r = 0.18 * winH * Math.sqrt(rand());
        const th = TAU * rand();
        px = cx + r * Math.cos(th);
        py = cy + r * Math.sin(th);
        break;
      }
      case "ring": {
        // Draws: theta, radius. Radius 0.32 +/- 0.025, in window units.
        const th = TAU * rand();
        const r = (0.32 + (rand() - 0.5) * 0.05) * winH;
        px = cx + r * Math.cos(th);
        py = cy + r * Math.sin(th);
        break;
      }
      case "edges": {
        // Draws: side, along-edge t, inset. Window edges, slightly inset.
        const side = Math.min(3, Math.floor(rand() * 4));
        const t = rand();
        const inset = (0.01 + 0.02 * rand()) * winH;
        if (side === 0) {
          px = ox + t * winW;
          py = oy + inset;
        } else if (side === 1) {
          px = ox + t * winW;
          py = oy + winH - inset;
        } else if (side === 2) {
          px = ox + inset;
          py = oy + t * winH;
        } else {
          px = ox + winW - inset;
          py = oy + t * winH;
        }
        break;
      }
      case "line": {
        // Draws: x, y. Horizontal center line, 2% of window height thick.
        px = ox + rand() * winW;
        py = cy + (rand() - 0.5) * 0.02 * winH;
        break;
      }
      default: {
        // "full" — draws: x, y. Uniform over the (zoomed) visible window.
        px = ox + rand() * winW;
        py = oy + rand() * winH;
        break;
      }
    }

    // age < 0 = staggered start: frozen and invisible until it reaches 0.
    const age = -(rand() * opts.stagger);
    const life = lifetimeRoll(opts.lifetime, opts.lifetimeJitter, rand());
    const sizeJ = rand();
    const paletteT = rand();
    const speedJ = rand();

    out[o] = px;
    out[o + 1] = py;
    out[o + 2] = age;
    out[o + 3] = life;
    out[o + 4] = sizeJ;
    out[o + 5] = paletteT;
    out[o + 6] = speedJ;
    out[o + 7] = 0; // unused; slot kept for layout compat
  }
  return out;
}
